import { useCallback } from "react";
import { useAtomValue, useAtom } from "jotai";
import { Node, NodeProps } from "@xyflow/react";
import classNames from "classnames";

import { isRemirrorJSONEmpty } from "@zapier/rich-text-editor";
import { DEFAULT_NODE_COLOR } from "@zapier/canvas-constants";
import {
  DefaultNodeData,
  isViewOnlyModeAtom,
  floatingToolbarActiveButtonAtom,
} from "@zapier/canvas-state";

import { StepTitle } from "../../../StepTitle";
import { NodeLinkDisplay } from "../../../NodeLinkDisplay";
import { ActorLabelPositioner } from "../../../ActorLabelPositioner";
import { EmbedContentThumbnail } from "../../../Embeds";
import { AssetNodeDeterminer } from "../AssetNodeSkeleton/AssetNodeDeterminer";
import { NotesButton } from "../AssetNodeSkeleton/NotesButton";

import styles from "./DefaultNode.module.css";

interface DefaultNodeContentProps {
  className: string;
  isViewOnlyMode?: boolean;
  backgroundColor?: string;
}

export function DefaultNodeContentInner({
  className,
  ...props
}: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps) {
  const { data, id, selected, backgroundColor } = props;
  const {
    label,
    color = DEFAULT_NODE_COLOR,
    link,
    displayLinkAs,
    actors,
  } = data;
  const actor = actors?.[0];
  const isViewOnlyMode = useAtomValue(isViewOnlyModeAtom);
  const [activeButton, setActiveButton] = useAtom(
    floatingToolbarActiveButtonAtom,
  );
  const hasNote = !isRemirrorJSONEmpty(data.notesJson);
  const showEmbedContent = Boolean(link && displayLinkAs === "embed");

  const dynamicStyles = {
    "--border-color": color,
    "--background-color": backgroundColor,
  };

  const toggleFloatingLinkInput = useCallback(() => {
    const isAlreadyOpen = "link" === activeButton;
    setActiveButton(isAlreadyOpen ? null : "link");
  }, [activeButton, setActiveButton]);

  return (
    <div
      className={classNames(styles.wrapper, className)}
      style={dynamicStyles as React.CSSProperties}
    >
      {actor && (
        <div className={styles.actorLabelWrapper}>
          <ActorLabelPositioner actor={actor} shouldOverlap={false} />
        </div>
      )}
      <div className={styles.body}>
        {showEmbedContent && <EmbedContentThumbnail url={link!} />}
        <StepTitle
          nodeId={id}
          label={label}
          isSelected={Boolean(selected)}
          canNodeGetAiSuggestion={true}
          isViewOnlyMode={isViewOnlyMode}
          rows={2}
        />
      </div>
      <div className={styles.flexRow}>
        {link && (
          <NodeLinkDisplay
            link={link}
            isViewOnlyMode={isViewOnlyMode}
            onLinkClick={toggleFloatingLinkInput}
            urlTruncateLength={20}
          />
        )}
        <NotesButton
          id={id}
          hasNotes={hasNote}
          isViewOnlyMode={isViewOnlyMode}
        />
      </div>
    </div>
  );
}

/* This is a presentational component - please keep all RF smarts in the outer DefaultNode component
 * We use this to render a node outside of RF.
 * */
export function DefaultNodeContent(
  props: NodeProps<Node<DefaultNodeData>> & DefaultNodeContentProps,
) {
  return <AssetNodeDeterminer {...props} />;
}
